import {
  useApprovedBackdatedHoursRequests,
  usePendingBackdatedHoursRequests,
} from '../../hooks/useBackdatedHoursRequests';
import { BackdatedHoursRequestsTable } from './BackdatedHoursRequestsTable';
import { BackdatedHoursCorrectionsTable } from './BackdatedHoursCorrectionsTable';

/**
 * Phase 113 (MKS-208, D-10) — the project-scoped half of the approval surface,
 * on the project detail page. `ProjectJoinRequestsPanel` is the analog, down to
 * hiding itself when there is nothing pending: this is a contextual action
 * surface, not a status dashboard.
 *
 * MKS-266 adds a second section: the entries this project's approvals already
 * created, correctable while the month stays locked.
 */
interface BackdatedHoursRequestsPanelProps {
  projectId: number;
  isAdmin: boolean;
}

export function BackdatedHoursRequestsPanel({
  projectId,
  isAdmin,
}: BackdatedHoursRequestsPanelProps) {
  const {
    data: requests,
    isLoading,
    error,
  } = usePendingBackdatedHoursRequests(isAdmin ? 'all' : undefined);
  const { data: approved, error: approvedError } = useApprovedBackdatedHoursRequests(projectId);

  // A transient managed-scope 403/500 must never break the project page; the
  // central queue stays the fallback.
  if (isLoading || error) return null;

  const projectRequests = (requests ?? []).filter((r) => r.projectId === projectId);
  // Same rule for the corrections half: a failed fetch hides that section
  // rather than taking the panel (or the page) down with it.
  const corrections = approvedError ? [] : (approved ?? []);

  if (projectRequests.length === 0 && corrections.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Backdated Hours Requests</h2>
        {projectRequests.length > 0 && (
          <span className="text-sm text-gray-500">
            {projectRequests.length} pending request{projectRequests.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {projectRequests.length > 0 && <BackdatedHoursRequestsTable requests={projectRequests} />}

      {corrections.length > 0 && (
        <div className={projectRequests.length > 0 ? 'mt-8' : undefined}>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Approved — entries created</h3>
          <p className="text-xs text-gray-500 mb-3">
            These entries sit in a locked month, so nobody can edit them from a timesheet. Correct
            the hours, the task or the description here; the change is audited.
          </p>
          <BackdatedHoursCorrectionsTable requests={corrections} />
        </div>
      )}
    </div>
  );
}

export default BackdatedHoursRequestsPanel;
